import React from 'react';
import { daysBetween, fmtDate, makeDate } from './data.jsx';
import { Icon } from './icons.jsx';
import { FEATURE_IMPERSONATION } from './mvp-config.jsx';
import { ApiErrorState, EmptyState, SkeletonTable, isRealTenantsMode, useTenants } from './services.jsx';
import { StatusBadge } from './shell.jsx';
import { BillingStatusBadge, OperationalStatusBadge, PlanBadge, TenantStatusBadge } from './tenant-status.jsx';
// Página de Lojas — gestão de tenants da plataforma Tezus
const downloadCSV = (filename, rows, headers) => {
  const escape = v => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g,'""')}"` : s; };
  const lines = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Normaliza status (pt → en) para filtro unificado
const normStatus = (s) => {
  const map = {
    ativo: 'active',
    inadimplente: 'overdue',
    past_due: 'overdue',
    suspenso: 'suspended',
    cancelado: 'cancelled',
    provisionando: 'provisioning',
  };
  return map[s] || s;
};

const LojasPage = ({ onNav, onImpersonate, onSuspend, onOperationalAction, listVersion = 0, layout = 'default', onNewStore, stores: propStores = [] }) => {
  const showImpersonation = FEATURE_IMPERSONATION === true;
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const listParams = React.useMemo(
    () => ({
      page: 1,
      page_size: 200,
      ...(includeArchived ? { include_archived: 'true' } : {}),
      _v: listVersion,
    }),
    [includeArchived, listVersion],
  );
  // useTenants como fonte primária (prepara para API real)
  const { data: hookStores, loading: hookLoading, error: hookError, refetch } =
    typeof useTenants !== 'undefined'
      ? useTenants(listParams)
      : { data: null, loading: false, error: null, refetch: () => {} };

  const useReal = typeof isRealTenantsMode === 'function' && isRealTenantsMode();
  const stores = hookStores ?? (useReal && hookError ? [] : propStores);
  const loading = hookLoading && !hookStores;
  const error   = hookError && !hookStores;

  const [query,       setQuery]       = React.useState('');
  const [filter,      setFilter]      = React.useState('all');
  const [planFilter,  setPlanFilter]  = React.useState('all');
  const [page,        setPage]        = React.useState(1);
  const [statusModal, setStatusModal] = React.useState(null);
  const perPage = 15;

  const filtered = React.useMemo(() => {
    let result = stores;
    if (filter === 'archived') {
      result = result.filter(s => (s.operational_status || 'active') === 'archived');
    } else if (filter !== 'all') {
      result = result.filter(s => normStatus(s.status) === filter);
    }
    if (planFilter !== 'all') result = result.filter(s => s.plan?.id === planFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      result = result.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.domain.toLowerCase().includes(q) ||
        (s.schema_name && s.schema_name.toLowerCase().includes(q)) ||
        (s.customDomain && s.customDomain.toLowerCase().includes(q)) ||
        (s.ownerName && s.ownerName.toLowerCase().includes(q))
      );
    }
    return result;
  }, [stores, filter, planFilter, query]);

  const paginated  = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  const counts = {
    all:                 stores.length,
    active:              stores.filter(s => normStatus(s.status) === 'active').length,
    trial:               stores.filter(s => normStatus(s.status) === 'trial').length,
    overdue:             stores.filter(s => normStatus(s.status) === 'overdue').length,
    suspended:           stores.filter(s => normStatus(s.status) === 'suspended').length,
    provisioning:        stores.filter(s => normStatus(s.status) === 'provisioning').length,
    provisioning_failed: stores.filter(s => normStatus(s.status) === 'provisioning_failed').length,
    archived:            stores.filter(s => (s.operational_status || 'active') === 'archived').length,
  };

  const today        = makeDate(0);
  const newThisMonth = stores.filter(s => s.createdAt && daysBetween(s.createdAt, today) <= 30).length;

  const KPI_CARDS = [
    { label: 'Ativos',        value: counts.active,              color: '#0f7a3e', filter: 'active' },
    { label: 'Trials',        value: counts.trial,               color: '#1d4ed8', filter: 'trial' },
    { label: 'Inadimplentes', value: counts.overdue,             color: '#b45309', filter: 'overdue' },
    { label: 'Suspensos',     value: counts.suspended,           color: '#EC2A58', filter: 'suspended' },
    { label: 'Falha prov.',   value: counts.provisioning_failed, color: '#EC2A58', filter: 'provisioning_failed' },
    { label: 'Novos/mês',     value: newThisMonth,               color: 'var(--text-secondary)', filter: null },
  ];

  const FILTER_TABS = [
    { id: 'all',                 label: 'Todos',         count: counts.all },
    { id: 'active',              label: 'Ativos',        count: counts.active },
    { id: 'trial',               label: 'Trial',         count: counts.trial },
    { id: 'overdue',             label: 'Inadimplentes', count: counts.overdue },
    { id: 'suspended',           label: 'Suspensos',     count: counts.suspended },
    { id: 'provisioning_failed', label: 'Falha prov.',   count: counts.provisioning_failed },
    { id: 'archived',            label: 'Arquivados',  count: counts.archived },
  ].filter(f => f.id === 'all' || f.count > 0 || f.id === 'archived');

  const handleExportCSV = () => {
    const headers = ['Loja', 'Domínio', 'Plano', 'Status', 'MRR', 'Próximo vencimento', 'Criada em'];
    const rows = filtered.map(s => [s.name, s.customDomain || s.domain, s.plan.name, s.status, `R$ ${s.mrr.toFixed(2)}`, fmtDate(s.nextDue), fmtDate(s.createdAt)]);
    downloadCSV('lojas-tezus.csv', rows, headers);
  };

  return (
    <div className="content">
      <div className="page-header">
        <div>
          <h1>Lojas</h1>
          <p className="sub">{filtered.length} de {stores.length} lojas</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={handleExportCSV}><Icon name="download" size={14} />Exportar CSV</button>
          <button className="btn primary" onClick={() => onNav({ page: 'nova-loja' })}><Icon name="store" size={14} />Nova loja</button>
        </div>
      </div>

      {/* Status summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 16 }}>
        {KPI_CARDS.map(k => (
          <div key={k.label}
            onClick={() => k.filter && (setFilter(k.filter), setPage(1))}
            style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '12px 14px', cursor: k.filter ? 'pointer' : 'default', transition: 'box-shadow 120ms', outline: filter === k.filter ? `2px solid ${k.color}` : 'none', outlineOffset: 2 }}
            onMouseEnter={e => k.filter && (e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: k.color, letterSpacing: '-0.02em' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', marginBottom: 16, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: 360 }}>
          <Icon name="search" size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input type="text" placeholder="Buscar lojas, domínios, owners..." value={query}
            onChange={e => { setQuery(e.target.value); setPage(1); }}
            style={{ width: '100%', height: 36, paddingLeft: 38, paddingRight: 12, border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, background: 'var(--bg)', outline: 'none', color: 'var(--text)' }}
          />
        </div>
        <select
          value={planFilter}
          onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
          style={{ height: 36, border: '1px solid var(--border)', borderRadius: 8, padding: '0 10px', background: 'var(--bg)', color: 'var(--text)', fontSize: 13 }}
        >
          <option value="all">Todos os planos</option>
          <option value="basic">Basic</option>
          <option value="pro">Pro</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={e => { setIncludeArchived(e.target.checked); setPage(1); }}
            style={{ width: 14, height: 14, accentColor: 'var(--primary)' }}
          />
          Incluir arquivados
        </label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTER_TABS.map(f => (
            <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }}
              style={{ padding: '5px 11px', borderRadius: 8, border: 'none', background: filter === f.id ? 'var(--brand-navy)' : 'var(--bg-subtle)', color: filter === f.id ? 'white' : 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              {f.label} <span style={{ fontSize: 11, opacity: 0.8 }}>({f.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tabela */}
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-subtle)', position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              {['Loja', 'Plano', 'Comercial', 'Operacional', 'MRR', 'Vencimento', 'Ações'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: h === 'MRR' ? 'right' : h === 'Ações' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Estado: carregando */}
            {loading && typeof SkeletonTable !== 'undefined' && <SkeletonTable rows={8} cols={7} />}

            {/* Estado: erro */}
            {error && !loading && (
              <tr><td colSpan={7} style={{ padding: 0 }}>
                {typeof ApiErrorState !== 'undefined'
                  ? <ApiErrorState error={hookError} onRetry={refetch} />
                  : <div style={{ padding: 32, textAlign: 'center', color: 'var(--danger)' }}>Erro ao carregar lojas. <button className="btn" onClick={refetch}>Tentar novamente</button></div>}
              </td></tr>
            )}

            {/* Estado: vazio */}
            {!loading && !error && paginated.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 0 }}>
                {typeof EmptyState !== 'undefined'
                  ? <EmptyState icon="store" title="Nenhuma loja encontrada" description="Ajuste os filtros ou crie uma nova loja." action={{ label: 'Nova loja', onClick: () => onNav({ page: 'nova-loja' }) }} />
                  : <div style={{ padding: '64px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      <Icon name="store" size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>Nenhuma loja encontrada</div>
                      <div style={{ fontSize: 13 }}>Ajuste os filtros ou crie uma nova loja.</div>
                    </div>}
              </td></tr>
            )}

            {/* Lista normal */}
            {!loading && !error && paginated.map(s => {
              const ns = normStatus(s.status);
              const opStatus = s.operational_status || 'active';
              const isFailed = ns === 'provisioning_failed';
              const isArchived = opStatus === 'archived';
              return (
                <tr key={s.id}
                  onClick={() => onNav({ page: 'loja', store: s })}
                  style={{ cursor: 'pointer', borderTop: '1px solid var(--border)', background: isFailed ? 'color-mix(in srgb, var(--danger-bg) 20%, transparent)' : '' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = isFailed ? 'color-mix(in srgb, var(--danger-bg) 20%, transparent)' : ''}
                >
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 8, background: s.color || `hsl(${(s.id || '').charCodeAt(5) * 37 % 360}, 60%, 50%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                        {s.name.split(' ').filter(w => w.length > 1).slice(0, 2).map(w => w[0]).join('')}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {s.name}
                          {isFailed && <Icon name="alert" size={13} style={{ color: 'var(--danger)', flexShrink: 0 }} />}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {s.customDomain || s.domain}
                          {s.schema_name ? ` · ${s.schema_name}` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {typeof PlanBadge !== 'undefined' ? <PlanBadge plan={s.plan} /> : <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.plan.name}</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {s.commercial_status && typeof BillingStatusBadge !== 'undefined'
                      ? <BillingStatusBadge status={s.commercial_status} label={s.commercial_status_label} />
                      : typeof TenantStatusBadge !== 'undefined'
                        ? <TenantStatusBadge status={ns} />
                        : <StatusBadge status={s.status} />}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {typeof OperationalStatusBadge !== 'undefined'
                      ? <OperationalStatusBadge status={opStatus} label={s.operational_status_label} />
                      : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{opStatus}</span>}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, fontSize: 14 }}>
                    {s.mrr > 0 ? `R$ ${s.mrr.toFixed(2).replace('.', ',')}` : '—'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{fmtDate(s.nextDue)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {s.health_summary && (
                        <span style={{
                          fontSize: 10,
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: s.health_summary.schema_accessible === false ? 'var(--danger-bg)' : 'var(--success-bg)',
                          color: s.health_summary.schema_accessible === false ? 'var(--danger)' : 'var(--success)',
                          alignSelf: 'center'
                        }}>
                          {s.health_summary.schema_accessible === false ? 'Schema issue' : 'OK'}
                        </span>
                      )}
                      {isFailed && !useReal && (
                        <button className="icon-action" title="Re-provisionar" onClick={() => { if (window.onToast) window.onToast('Re-provisionamento iniciado'); }}>
                          <Icon name="refresh" size={14} />
                        </button>
                      )}
                      {showImpersonation && (
                        <button className="icon-action" onClick={() => onImpersonate(s)} title="Login como lojista" disabled={ns === 'suspended' || ns === 'suspenso'}>
                          <Icon name="switchUser" size={14} />
                        </button>
                      )}
                      <button className="icon-action" onClick={() => onSuspend(s)} title="Alterar status comercial">
                        <Icon name="cardCoin" size={14} />
                      </button>
                      {onOperationalAction && !isArchived && (
                        <button className="icon-action" onClick={() => onOperationalAction(s, 'archive')} title="Arquivar loja">
                          <Icon name="fileText" size={14} />
                        </button>
                      )}
                      {onOperationalAction && isArchived && (
                        <button className="icon-action" onClick={() => onOperationalAction(s, 'reactivate')} title="Reativar loja">
                          <Icon name="play" size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
          <div>Mostrando {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} de {filtered.length}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn ghost" style={{ padding: '6px 12px', fontSize: 13 }}>Anterior</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, page - 3), Math.min(totalPages, page + 2)).map(p => (
              <button key={p} onClick={() => setPage(p)} className={p === page ? 'btn primary' : 'btn ghost'} style={{ padding: '6px 12px', fontSize: 13, minWidth: 36 }}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn ghost" style={{ padding: '6px 12px', fontSize: 13 }}>Próximo</button>
          </div>
        </div>
      )}
    </div>
  );
};

export {
  LojasPage,
  normStatus,
};
